import { DateTime } from 'luxon'
import { SeriesGroup } from './series'
import { ScaleLabeling } from '../types/charts'
import { TimeString } from '../types/models'

/**
 * Helpers for the lazily paged history charts (see `PortHistory`).
 */

/**
 * Parse an API timestamp into epoch milliseconds.
 *
 * History timestamps are UTC, but some endpoints serialize them without the
 * trailing `Z` (naive stamps). Luxon would read those as local time, so add
 * the zone marker when it is missing.
 */
export function stampMs(stamp: TimeString | string): number {
  const iso = /(Z|[+-]\d{2}:?\d{2})$/.test(stamp) ? stamp : `${stamp}Z`
  return DateTime.fromISO(iso).toMillis()
}

/**
 * Merge a freshly fetched page of history points into the already loaded
 * series, keyed by timestamp.
 *
 * The pages are requested with an inclusive `from`/`to` range, and the server
 * answers with whole buckets whose start time falls inside it. That means the
 * boundary point is duplicated when the requested edge happens to be bucket
 * aligned, and *not* duplicated when it is not (which is the normal case after
 * a scale switch, where the fetch window is centered on an arbitrary anchor
 * time) or when the bucket at the edge simply has no data. Blindly trimming
 * one point off the page therefore either left a duplicate or, far more often,
 * punched a one-bucket hole into the series. Deduplicating by timestamp is
 * correct in every case.
 */
export function mergeHistory<T extends { timestamp: TimeString }>(existing: T[], fresh: T[]): T[] {
  const byStamp = new Map<number, T>()
  for (const point of existing) {
    byStamp.set(stampMs(point.timestamp), point)
  }
  for (const point of fresh) {
    byStamp.set(stampMs(point.timestamp), point)
  }
  return [...byStamp.entries()].sort((a, b) => a[0] - b[0]).map(([, point]) => point)
}

/**
 * Index offset that keeps the same wall-clock window visible after the data
 * behind a chart changed.
 *
 * The chart's x scale is index based, so prepending groups pushes the existing
 * ones to the right by exactly the number of groups added in front, while
 * appending does not move them at all. Locating the previously leftmost group
 * by its start time gives that offset directly; counting how much the data
 * array grew does not, because it cannot tell a prepend from an append and
 * because empty buckets become groups without contributing data points.
 *
 * Returns -1 when the old leftmost group is gone (data reset, scale switch),
 * meaning the previous viewport can no longer be preserved.
 */
export function viewportShift(prevFirstFrom: number | null, groups: SeriesGroup[]): number {
  if (prevFirstFrom === null) return -1
  // Groups are contiguous, so the first one that has not ended yet at that
  // instant is the one the old leftmost group turned into. Matching on the
  // start time alone would miss the case where prepending data across a DST
  // boundary moves every bucket edge by an hour.
  return groups.findIndex(group => group.to > prevFirstFrom)
}

/**
 * The points a chart drawn at `scale` may use.
 *
 * Points are always fetched for one particular scale, and `Series` buckets them
 * into one slot per step between the first and the last point. Charting points
 * of a coarser scale with a finer `steps` therefore produces a slot count of
 * the two scales' ratio - four months of week data redrawn as minutes is about
 * 185 000 slots, which locks the page up and blows the call stack. That pairing
 * is never wanted: it happens only transiently, on the render right after a
 * scale switch (before the effect that refetches has run) or when a response
 * for the previous scale arrives late. Return nothing until the points and the
 * scale agree; the chart shows its spinner meanwhile.
 */
export function pointsForScale<T>(points: T[], pointsScale: ScaleLabeling | null, scale: ScaleLabeling): T[] {
  return pointsScale === scale ? points : []
}
